import * as React from 'react'
import { useLocale } from 'hooks'

const applyWidth = (src, width) => {
  try {
    const url = new URL(src)

    url.searchParams.set('width', String(width))
    return url.toString()
  } catch {
    const separator = src.includes('?') ? '&' : '?'

    return `${src}${separator}width=${width}`
  }
}

/**
 * @param {HeroItemProps} HeroItemProps - The component props.
 * @returns {JSX.Element} - The rendered component.
 */
const HeroItem = ({
  invertTextColor,
  position,
  active,
  title,
  subtitle,
  content,
  mobileImgSrc,
  imgSrc,
  link,
  buttonText
}) => {
  const { localiseUrl } = useLocale()

  const [y, x] = position.replace(/_/g, ' ').trim().split(/\s/)

  return (
    <div
      className={`hero-item ${invertTextColor ? 'hero-item--dark' : ''} ${
        active ? 'hero-item--active' : ''
      }`}
    >
      <div
        className={`hero-item__info-container hero-item__info-container--${x}`}
      >
        <div
          className={`hero-item__info hero-item__info--${x} hero-item__info--${y}`}
        >
          <h1
            className="hero-item__title title"
            dangerouslySetInnerHTML={{ __html: title }}
          />
          {subtitle ? (
            <h3 className="hero-item__subheading subheading">{subtitle}</h3>
          ) : null}
          {content ? (
            <p className="hero-item__footnote footnote">{content}</p>
          ) : null}
          {buttonText && (
            <a
              className="hero-item__cta btn btn--primary"
              href={localiseUrl(link)}
            >
              {buttonText}
            </a>
          )}
        </div>
      </div>
      <div className="hero-item__image">
        {imgSrc && mobileImgSrc && (
          <picture>
            {[
              300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400
            ].map((width) => {
              const baseSrc = width > 500 ? imgSrc : mobileImgSrc

              return (
                <source
                  key={width}
                  srcSet={applyWidth(baseSrc, width)}
                  media={`(max-width: ${width}px)`}
                  width={width}
                  height={550}
                />
              )
            })}
            {/* fallback image */}
            <img
              src={applyWidth(imgSrc, 1440)}
              width={1440}
              alt={title}
              // eslint-disable-next-line react/no-unknown-property
              fetchPriority={active ? 'high' : 'low'}
            />
          </picture>
        )}
      </div>
    </div>
  )
}

export default HeroItem

/**
 * @typedef {{
 * position: string,
 * active: boolean,
 * title: string,
 * subtitle: string,
 * content: string,
 * mobileImgSrc: string,
 * imgSrc: string,
 * invertTextColor: boolean,
 * link: string
 * buttonText: string
 * }} HeroItemProps
 */
